/**
 * confirmDialog()
 * ───────────────
 * A promise-based replacement for window.confirm() that renders the app's
 * own Dialog. Usable from any handler, hook or not:
 *
 *   if (!(await confirmDialog(t('helpdesk.delete_ticket_confirm')))) return;
 *
 *   const ok = await confirmDialog({
 *     title: t('crm.delete_lead'),
 *     body: t('crm.delete_lead_body'),
 *     confirmLabel: t('common.delete'),
 *     danger: true,
 *   });
 *
 * A plain string becomes the body under a generic "Please confirm" title.
 * Resolves true on confirm, false on cancel, Escape or backdrop click.
 * <ConfirmHost /> must be mounted once (App.js) for this to render; until
 * it mounts, calls fall back to window.confirm so nothing is ever lost.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LuTriangleAlert, LuCircleHelp } from 'react-icons/lu';
import Dialog from './Dialog';
import Button from './Button';
import useTranslation from '../../hooks/useTranslation';

let host = null;

export const confirmDialog = (options) => {
  // A bare string (the codemod'd window.confirm sites) gets its tone inferred
  // from the wording: destructive verbs get the red button and a Delete label.
  const opts = typeof options === 'string' ? { body: options, inferred: true } : { ...(options || {}) };
  if (!host) {
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(opts.body || opts.title || ''));
  }
  return host(opts);
};

export const ConfirmHost = () => {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // { opts, resolve }
  const confirmRef = useRef(null);

  useEffect(() => {
    host = (opts) => new Promise((resolve) => {
      setState((prev) => {
        // A second request while one is open cancels the first.
        prev?.resolve(false);
        return { opts, resolve };
      });
    });
    return () => { host = null; };
  }, []);

  const settle = useCallback((value) => {
    setState((prev) => { prev?.resolve(value); return null; });
  }, []);

  const opts = state?.opts || {};
  const inferredDanger = !!opts.inferred && /\b(delete|remove|revoke|discard|permanent|clear all|reset)\b/i.test(opts.body || '');
  const danger = opts.danger ?? inferredDanger;
  const confirmLabel = opts.confirmLabel || (inferredDanger && /\bdelete\b/i.test(opts.body || '') ? t('common.delete') : t('common.confirm'));
  const title = opts.title || t('common.confirm_title');
  const Icon = danger ? LuTriangleAlert : LuCircleHelp;

  return (
    <Dialog
      open={!!state}
      onClose={() => settle(false)}
      size="sm"
      showClose={false}
      initialFocusRef={danger ? undefined : confirmRef}
      footer={(
        <>
          <Button variant="secondary" onClick={() => settle(false)}>
            {opts.cancelLabel || t('common.cancel')}
          </Button>
          <Button ref={confirmRef} variant={danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
            {confirmLabel}
          </Button>
        </>
      )}
    >
      <div className="flex items-start gap-4">
        <span
          className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${danger ? 'bg-danger-bg-2 text-danger' : 'bg-accent-soft text-accent'}`}
          aria-hidden="true"
        >
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <h2 className="text-[16px] font-bold text-ink m-0 leading-6">{title}</h2>
          {opts.body && <p className="text-sm text-text-2 m-0 mt-1.5 leading-relaxed whitespace-pre-line">{opts.body}</p>}
        </div>
      </div>
    </Dialog>
  );
};

export default confirmDialog;
